import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Download } from "lucide-react";
import { downloadVCard } from "../../lib/vcard";

const easing = [0.22, 1, 0.36, 1];

function MaskLine({ children, delay }) {
  return (
    <span className="reveal-line">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: easing, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ hero, contact, appr }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const dial = contact?.phone_dial || "+4962515504920";

  return (
    <section ref={ref} style={{ paddingBottom: `${appr?.h ?? 40}px`, fontSize: `${appr?.t ?? 16}px`, backgroundColor: appr?.bg }} className={`relative pt-[104px] lg:pt-[120px] overflow-hidden lg:flex lg:items-center ${appr?.dark ? "mf-section-dark" : ""}`}>
      <div className="mf-container w-full grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* left */}
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
            <span className="mf-eyebrow mb-7">{hero?.eyebrow}</span>
          </motion.div>

          <h1 className="font-head font-light tracking-tight text-[var(--mf-ink)] text-[1.875em] sm:text-[2.25em] lg:text-[3.15em] leading-[1.06] mt-5">
            <MaskLine delay={0.15}>
              <span className="font-medium">{hero?.h1_line1}</span>
            </MaskLine>
            <MaskLine delay={0.32}>
              <span className="text-[var(--mf-grey)]">{hero?.h1_line2}</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: easing, delay: 0.6 }}
            className="mt-8 max-w-xl text-[1em] sm:text-[1.125em] leading-relaxed text-[var(--mf-ink-soft)]"
          >
            {hero?.subheadline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: easing, delay: 0.78 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a href={`tel:${dial}`} data-testid="hero-call-btn" className="mf-cta">
              <Phone size={18} strokeWidth={2.4} /> {hero?.cta_primary || "Anrufen"}
            </a>
            <button onClick={() => downloadVCard(contact)} data-testid="hero-secondary-btn" className="mf-cta-ghost">
              {hero?.cta_secondary || "Kontakt speichern"} <Download size={18} />
            </button>
          </motion.div>
        </div>

        {/* right image */}
        <div className="lg:col-span-5">
          <div className="relative h-[300px] sm:h-[420px] lg:h-[440px] overflow-hidden">
            <motion.img
              src="/images/medizintechnik-vernetzt.webp"
              alt="Vernetzte Medizintechnik und Patientenmonitore auf einer Station"
              style={{ y: imgY }}
              className="absolute inset-0 w-full h-[112%] object-cover object-center"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
          </div>
        </div>
      </div>
    </section>
  );
}
